/**
 * LegalLayout.tsx — shared shell for the ɳSentry SaaS legal documents
 *
 * Purpose:    Consistent dark-first chrome for /legal/terms, /legal/privacy,
 *             and /legal/gdpr: title, effective date, "review with counsel"
 *             banner, cross-document nav, and typographic defaults for the
 *             section content each page provides.
 * Inputs:     title, lastUpdated (ISO date), children (h2-sectioned content)
 * Outputs:    Rendered legal document inside the marketing SiteLayout
 * Constraints: WCAG AA (nav landmarks, focus-visible, contrast); no external
 *              typography plugin — styles are local utility classes.
 * SOT:        nsentry SaaS completion spec § G-WEB (legal)
 */
import { NavLink } from 'react-router-dom'

const LEGAL_DOCS = [
  { to: '/legal/terms', label: 'Terms of Service' },
  { to: '/legal/privacy', label: 'Privacy Policy' },
  { to: '/legal/gdpr', label: 'GDPR & Data Processing' },
] as const

export function LegalLayout({
  title,
  lastUpdated,
  children,
}: {
  title: string
  lastUpdated: string
  children: React.ReactNode
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <nav aria-label="Legal documents" className="mb-8 flex flex-wrap gap-2">
        {LEGAL_DOCS.map((doc) => (
          <NavLink
            key={doc.to}
            to={doc.to}
            className={({ isActive }) =>
              [
                'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400',
                isActive
                  ? 'border-sky-600 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300'
                  : 'border-brand-subtle text-brand-muted hover:border-brand-card hover:text-white',
              ].join(' ')
            }
          >
            {doc.label}
          </NavLink>
        ))}
      </nav>

      <h1 className="text-3xl font-bold text-white sm:text-4xl">{title}</h1>
      <p className="mt-3 text-sm text-brand-muted">
        Last updated: <time dateTime={lastUpdated}>{lastUpdated}</time>
      </p>

      <p className="mt-6 rounded-lg border border-yellow-900/50 bg-yellow-50 dark:bg-yellow-950/30 px-4 py-3 text-sm text-yellow-700 dark:text-yellow-200/90">
        This is a standard SaaS template published for transparency during ɳSentry&apos;s
        early-access period. It has not yet been reviewed by legal counsel and will be
        finalized with counsel before general availability.
      </p>

      <div className="legal-body mt-10 space-y-10 text-[0.95rem] leading-relaxed text-brand-secondary [&_a]:text-sky-700 dark:text-sky-400 [&_a:hover]:text-sky-700 dark:text-sky-300 [&_h2]:scroll-mt-24 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-brand-ink [&_li]:mt-1.5 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6">
        {children}
      </div>

      <p className="mt-14 border-t border-brand-subtle pt-6 text-sm text-brand-muted">
        Questions about these documents? Email{' '}
        <a href="mailto:legal@nself.org" className="text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
          legal@nself.org
        </a>
        . The ɳSelf project&apos;s master legal documentation lives at{' '}
        <a href="https://nself.org/legal" className="text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
          nself.org/legal
        </a>
        .
      </p>
    </div>
  )
}
